import { Check, Trash2, X } from 'lucide-react';

import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Toggle,
} from '@src/components/ui';


/**
 * Settings switch. Interactive switches use the shared `Toggle` primitive (T8);
 * the read-only variant (e.g. a disabled-channel row for non-managers) renders a
 * static pill since `Toggle` is always interactive. Keeps the original
 * `checked` / `onChange()` / `readOnly` / `label` API so callers are unchanged.
 */
function Switch({ checked, onChange, disabled, readOnly, label }) {
  if (readOnly) {
    return (
      <span
        aria-label={label}
        aria-checked={checked}
        className={cn(
          'inline-flex h-[26px] w-11 shrink-0 items-center rounded-full p-[3px]',
          checked ? 'justify-end bg-primary' : 'justify-start bg-hairline-strong',
        )}
        role="img"
      >
        <span className="block size-5 rounded-full bg-white shadow-sm" />
      </span>
    );
  }

  return (
    <Toggle
      checked={checked}
      aria-label={label}
      disabled={disabled}
      onCheckedChange={() => onChange()}
    />
  );
}

Switch.displayName = 'Switch';


/**
 * Centered modal shell reused by the channel + location editors and the Security
 * transfer/delete modals. Built on the shared `Dialog` primitive (focus trap,
 * ESC, scroll-lock, `bg-scrim`). `footer` renders pinned at the bottom. The
 * desktop card / mobile full-screen split is preserved via `contentClassName`.
 */
function Modal({ title, subtitle, onClose, children, footer, width = 'lg:max-w-[480px]' }) {
  return (
    <Dialog open onOpenChange={(next) => (next ? null : onClose())}>
      <DialogContent
        variant="modal"
        className={cn(
          'max-lg:inset-0 max-lg:left-0 max-lg:top-0 max-lg:h-full max-lg:w-full max-lg:max-w-none',
          'max-lg:translate-x-0 max-lg:translate-y-0 max-lg:rounded-none max-lg:border-0',
          'gap-0 p-0',
          width,
        )}
      >
        <DialogHeader className="flex flex-row items-start justify-between gap-3 px-[22px] pb-3.5 pt-5">
          <span className="flex flex-col gap-1">
            <DialogTitle className="text-lg tracking-[-0.3px]">{title}</DialogTitle>

            {subtitle
              ? <DialogDescription className="text-[13px] text-ink-muted">{subtitle}</DialogDescription>
              : null}
          </span>

          <Button
            size="icon"
            type="button"
            variant="ghost"
            aria-label="Close"
            className="size-[30px] shrink-0 rounded-md bg-surface-2"
            onClick={onClose}
          >
            <X className="size-4 text-ink-muted" />
          </Button>
        </DialogHeader>

        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[22px] pb-2.5">
          {children}
        </div>

        {footer
          ? (
            <DialogFooter className="border-t border-hairline px-[22px] py-3.5">
              {footer}
            </DialogFooter>
          )
          : null}
      </DialogContent>
    </Dialog>
  );
}

Modal.displayName = 'Modal';


/** Small confirm dialog — destructive (`danger`, default) or affirmative
 * (`primary`, e.g. plan activation). Built on the shared `Dialog`/`Button`
 * primitives. */
function ConfirmDialog(props) {
  const { title, body, confirmLabel = 'Delete', variant = 'danger', icon, busy, onCancel, onConfirm } = props;
  // Default icon adapts to intent: affirmative confirms (e.g. plan activation,
  // change-role) get a check, destructive ones a trash. Callers can override.
  const Icon = icon || (variant === 'primary' ? Check : Trash2);

  return (
    <Dialog open onOpenChange={(next) => (next ? null : onCancel())}>
      <DialogContent className="max-w-[420px] max-lg:rounded-[16px]">
        <DialogHeader className="max-lg:items-center max-lg:text-center">
          {/* Icon circle is a mobile-only affordance (.pen M Confirm Delete);
              desktop confirm dialogs stay icon-less + left-aligned (unchanged). */}
          <span
            className={cn(
              'grid size-[52px] place-items-center rounded-full lg:hidden',
              variant === 'primary' ? 'bg-primary/15 text-primary' : 'bg-danger/15 text-danger',
            )}
          >
            <Icon className="size-6" />
          </span>

          <DialogTitle>{title}</DialogTitle>

          {body
            ? <DialogDescription className="text-[13.5px] text-ink-muted">{body}</DialogDescription>
            : null}
        </DialogHeader>

        <DialogFooter className="max-lg:flex-col-reverse max-lg:gap-2.5">
          <Button type="button" variant="ghost" className="max-lg:w-full" onClick={onCancel}>
            Cancel
          </Button>

          <Button
            type="button"
            className="max-lg:w-full"
            variant={variant === 'primary' ? 'primary' : 'danger'}
            loading={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ConfirmDialog.displayName = 'ConfirmDialog';


/** Pull the first human-readable error string out of a DRF error payload. */
function firstApiError(data, ...keys) {
  if (!data || typeof data !== 'object') {
    return null;
  }
  for (const key of [...keys, 'detail', 'non_field_errors']) {
    const value = data[key];
    if (Array.isArray(value) && value.length) {
      return value[0];
    }
    if (typeof value === 'string') {
      return value;
    }
  }
  return null;
}


export { ConfirmDialog, firstApiError, Modal, Switch };
